import React, { useEffect, useRef, useState } from "react";

import Card from "../../../user/components/Card";
import CategorySelectDropdown from "../../../user/components/CategorySelectDropdown";
import { ChevronLeft, ChevronRight, X, ChevronDown, Search } from "lucide-react"; // optional icons
import StreamOnAccountSection from "../../components/StreamAccount";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, Form, Formik } from "formik";
import Swal from "sweetalert2";
import { SWAL_SETTINGS, SOCKET_EVENTS, ROLE } from "../../../../utils/Constants";
import { formatDateAndTime, handleServerValidations, getUser, setLocalKey } from "../../../../utils/commonfunction";
import { createAndForwardStream, createStream, GetLiveCaptions, startStream, GetStreamsDetails, initMultipartUpload, uploadMultipartChunk, completeMultipartUpload, getProductList, deleteStream } from "../../services/stream.service";
import { profileDetails, staffList } from "../../services/user.service";
import * as Yup from "yup";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { getSocialMedia, toggleSocialMedia } from "../../services/socialmedia.service";
import { GetCategoryList } from "../../services/category.service";
import { io } from "socket.io-client";
import { SOCKET_CLIENT } from "../../../../utils/socket";
import { useSelector } from "react-redux";

function Streams() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const videoInputRef = useRef(null);
  const [streamDetails, setStreamDetails] = useState();
  const [search, setSearch] = useState("");
  const [starting, setStarting] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const debounceRef = useRef(null);
  const userData = getUser(ROLE.ADVERTISER) || getUser(ROLE.USER) || {};
  const [data, setData] = useState(null);
  const [socialMediaData, setSocialMediaData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [productPage, setProductPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const formikRef = useRef(null);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [productSearch, setProductSearch] = useState("");
  const [isProductListOpen, setIsProductListOpen] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const productDropdownRef = useRef(null);

  const [globalData, setGlobalData] = useState({});
  const myGlobalData = useSelector((state) => state.globalData);
  useEffect(() => {
    if (myGlobalData && myGlobalData?.data) {
      const settingsData = JSON.parse(myGlobalData?.data);
      setGlobalData(settingsData);
    }
  }, [myGlobalData]);

  const [pastPage, setPastPage] = useState(1);
  const [hasMorePast, setHasMorePast] = useState(true);
  const [isFetchingMorePast, setIsFetchingMorePast] = useState(false);
  const pastObserverTarget = useRef(null);

  const loadStreams = async (searchValue = "", pageNum = 1, append = false) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setIsFetchingMorePast(true);
    }

    try {
      const payload = {
        search: searchValue,
        tab: pageNum > 1 ? "past" : "all",
        page: pageNum,
        limit: 12
      };

      const res = await GetStreamsDetails(payload);
      if (res.success && res.data) {
        if (append && pageNum > 1) {
          const incomingPast = res.data.past || [];
          setStreamDetails((prev) => {
            const existing = prev?.past || [];
            const existingIds = new Set(existing.map((item) => String(item._id)));
            const uniqueIncoming = incomingPast.filter((item) => !existingIds.has(String(item._id)));
            return {
              ...prev,
              past: [...existing, ...uniqueIncoming],
            };
          });
          setHasMorePast(res.data.pagination?.hasMore ?? (incomingPast.length >= 12));
        } else {
          setStreamDetails(res.data);
          const pastCount = res.data.past?.length || 0;
          setHasMorePast(res.data.pagination?.hasMore ?? (pastCount >= 12));
        }
      }
    } catch (err) {
      console.error("loadStreams error:", err);
    } finally {
      setLoading(false);
      setIsFetchingMorePast(false);
    }
  };

  useEffect(() => {
    const checkDevices = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          setHasCamera(false);
          return;
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setHasCamera(videoDevices.length > 0);
      } catch (err) {
        console.error('Error checking devices:', err);
      }
    };

    checkDevices();
    navigator.mediaDevices?.addEventListener('devicechange', checkDevices);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', checkDevices);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const res = await profileDetails({ _id: userData._id });
        if (res.success) {
          const balanceUpdate = {
            remaining_ai_clicks: res.data?.remaining_ai_clicks || 0,
            total_ai_clicks: res.data?.total_ai_clicks || 0,
            used_ai_clicks: res.data?.used_ai_clicks || 0,
            remaining_hours: res.data?.remaining_hours || 0,
            total_hours: res.data?.total_hours || 0,
          };
          const advertiserKey = getUser(ROLE.ADVERTISER) || {};
          const userKey = getUser(ROLE.USER) || {};
          setLocalKey(ROLE.ADVERTISER, JSON.stringify({ ...advertiserKey, ...balanceUpdate }));
          setLocalKey(ROLE.USER, JSON.stringify({ ...userKey, ...balanceUpdate }));
          setData(res.data); // Update local state with fresh profile data
        }
      } catch (err) {
        console.error("Fetch Balance Error:", err);
      }
    };
    fetchBalance();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPastPage(1);
      setHasMorePast(true);
      loadStreams(search, 1, false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePast && !loading && !isFetchingMorePast) {
          setPastPage((prev) => {
            const next = prev + 1;
            loadStreams(search, next, true);
            return next;
          });
        }
      },
      { threshold: 0.1, rootMargin: "250px" }
    );

    const currentTarget = pastObserverTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasMorePast, loading, isFetchingMorePast, search]);

  useEffect(() => {
    const handleBroadcast = (broadcast) => {
      console.log(broadcast, 'broadcastbroadcast');

      if (broadcast?.event === SOCKET_EVENTS.STREAM_STARTED) {
        loadStreams(search);
      }
      if (broadcast?.event === SOCKET_EVENTS.STREAM_ENDED) {
        loadStreams(search);
      }
    };

    SOCKET_CLIENT.on(SOCKET_EVENTS.GET_BROADCAST, handleBroadcast);

    return () => {
      SOCKET_CLIENT.off(SOCKET_EVENTS.GET_BROADCAST, handleBroadcast);
    };
  }, [search]);


  const fetchSocialMedia = async () => {
    try {
      const res = await getSocialMedia({});
      if (res?.success) setSocialMediaData(res.data);
    } catch (error) {
      console.log("social media Fetch Error:", error);
    }
  };

  useEffect(() => {
    fetchSocialMedia();
  }, []);

  const closeScheduleModal = (resetForm) => {
    const dialog = document.getElementById("schedule-a-stream");

    if (dialog) dialog.close();

    resetForm();
    setPreview(null);
    setVideoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    // Attempt to reset the unknown video file input as well by querying the DOM 
    // since it does not have a ref attached to it.
    const videoInput = document.querySelector('input[type="file"][accept="video/*"]');
    if (videoInput) videoInput.value = "";
  };

  const fetchStaff = async (search = "") => {
    setError(null);
    try {
      const resp = await staffList({ global_search: search });
      setStaff(resp.data.list);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to fetch staff");
    } finally {
    }
  };

  const fetchProducts = async (page = 1, search = "", reset = false) => {
    if (loadingProducts && !reset) return;
    setLoadingProducts(true);
    try {
      const payload = {
        page: page,
        per_page: 15,
      };
      if (search && search.trim()) {
        payload.search = JSON.stringify({ title: search.trim() });
      }

      const resp = await getProductList(payload);
      if (resp.success && resp.data?.list) {
        const newProducts = resp.data.list.map(p => ({
          value: p._id,
          label: p.title,
          isPublished: p.isPublished,
          status: p.status,
          image: p.image,
          price: p.price
        }));

        if (page === 1 || reset) {
          setProductsList(newProducts);
        } else {
          setProductsList(prev => {
            const existingIds = new Set(prev.map(p => p.value));
            const filtered = newProducts.filter(p => !existingIds.has(p.value));
            return [...prev, ...filtered];
          });
        }

        const totalRecords = resp.data.total_records || 0;
        setHasMoreProducts(page * 15 < totalRecords && newProducts.length > 0);
        setProductPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch products", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(1, productSearch, true);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const handleProductScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 40 && hasMoreProducts && !loadingProducts) {
      fetchProducts(productPage + 1, productSearch, false);
    }
  };

  useEffect(() => {
    fetchStaff();
    GetCategoryList({ filter_by_user: true })
      .then((data) => {
        if (data.list) {
          setCategoryList(data.list);
        }
      })
      .catch((error) => {
        console.log("Category Fetch error ====> ", error);
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setIsProductListOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const handleStartStream = async (item) => {
    if (starting) return;

    try {
      setStarting(true);

      const startResp = await startStream({ _id: item._id });
      if (!startResp.success) return;

      const stream = startResp.data.stream;

      await createAndForwardStream({
        zegoStreamId: stream._id,
      });

      navigate(
        `/advertiser/streams/live-streams/${stream.seller_id}/${stream.room_id}/${stream._id}`
      );

    } catch (err) {
      console.error(err);
    } finally {
      setStarting(false);
    }
  };

  const ScheduleStreamSchema = Yup.object().shape({
    title: Yup.string()
      .required(t("streams.validation.title_required"))
      .max(100, t("streams.validation.title_max")),
    date: Yup.string().when("scheduleType", {
      is: "custom",
      then: (schema) => schema.required(t("streams.validation.date_required")),
      otherwise: (schema) => schema.notRequired(),
    }),

    time: Yup.string()
      .when("scheduleType", {
        is: "custom",
        then: (schema) => schema.required(t("streams.validation.time_required")),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "past_time_error",
        t("streams.validation.past_time_error"),
        function (value) {
          const { date, scheduleType } = this.parent;

          // ✅ Skip validation if not custom
          if (scheduleType !== "custom") return true;

          if (!date || !value) return true;

          const now = new Date();

          const [hours, minutes] = value.split(":");

          const selectedDate = new Date(date);
          selectedDate.setHours(Number(hours));
          selectedDate.setMinutes(Number(minutes));
          selectedDate.setSeconds(0);

          return selectedDate.getTime() > now.getTime();
        }
      ),
    duration: Yup.number()
      .typeError(t("streams.validation.duration_required"))
      .positive(t("streams.validation.duration_positive"))
      .integer(t("streams.validation.duration_whole"))
      .required(t("streams.validation.duration_required")),

    assignToStaff: Yup.boolean(),
    generateByAI: Yup.boolean(),

    selectedProducts: Yup.array().test(
      'is-required',
      t("streams.validation.product_required"),
      (value, context) => {
        const { generateByAI } = context.parent;
        if (!generateByAI) {
          return value && value.length > 0;
        }
        return true;
      }
    ),

    staffMember: Yup.string().test(
      'is-required',
      t("streams.validation.staff_required"),
      (value, context) => {
        const { generateByAI, assignToStaff } = context.parent;
        if (generateByAI && assignToStaff) {
          return !!value;
        }
        return true;
      }
    ),

    streamType: Yup.string().required(t("streams.validation.stream_type_required")),

    description: Yup.string()
      .required(t("streams.validation.description_required"))
      .max(500, t("streams.validation.description_max")),

    thumbnail: Yup.mixed()
      .required(t("streams.validation.thumbnail_required"))
      .test(
        "file-size",
        t("streams.validation.file_size_error"),
        (value) => (value ? value.size <= 2 * 1024 * 1024 : false)
      ),

  }).test("single-or-multistream-check", null, function (values) {
    const { facebookEnabled, youtubeEnabled, twitchEnabled } = values;
    const selectedCount = (facebookEnabled ? 1 : 0) + (youtubeEnabled ? 1 : 0) + (twitchEnabled ? 1 : 0);

    if (String(globalData?.show_single_stream) === "false" && selectedCount === 0) {
      return this.createError({
        message: t("streams.validation.at_least_one_platform_required", "At least one streaming platform must be selected when Single Stream is disabled."),
        path: "facebookEnabled"
      });
    }

    if (String(globalData?.show_multistream) === "false" && selectedCount > 1) {
      return this.createError({
        message: t("streams.validation.multistream_disabled", "Multistreaming is disabled. You can select at most one streaming platform."),
        path: "facebookEnabled"
      });
    }

    return true;
  });
  const handleDateTimeFormat = (values) => {
    if (values.scheduleType === "now") {
      return new Date().toISOString();
    }

    let date = values.date;
    let time = values.time;

    const now = new Date();

    // ✅ HANDLE AUTO MODES HERE

    if (values.scheduleType === "5min") {
      const updated = new Date(now.getTime() + 5 * 60000);
      date = updated.toISOString().split("T")[0];
      time = updated.toTimeString().slice(0, 5);
    }

    if (values.scheduleType === "15min") {
      const updated = new Date(now.getTime() + 15 * 60000);
      date = updated.toISOString().split("T")[0];
      time = updated.toTimeString().slice(0, 5);
    }

    if (values.scheduleType === "25min") {
      const updated = new Date(now.getTime() + 25 * 60000);
      date = updated.toISOString().split("T")[0];
      time = updated.toTimeString().slice(0, 5);
    }

    if (values.scheduleType === "30min") {
      const updated = new Date(now.getTime() + 30 * 60000);
      date = updated.toISOString().split("T")[0];
      time = updated.toTimeString().slice(0, 5);
    }

    // ❌ Safety check
    if (!date || !time) {
      throw new Error("Invalid time value");
    }

    return new Date(`${date}T${time}`).toISOString();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (values.scheduleType === "now" && values.streamType === "live" && !hasCamera) {
        Swal.fire({
          icon: "error",
          text: t("streams.camera_not_detected_swal"),
          ...SWAL_SETTINGS,
        });
        return;
      }
      setSubmitting(true);
      const isoDate = handleDateTimeFormat(values);
      const formData = new FormData();

      // BASIC FIELDS
      formData.append("title", values.title);
      formData.append("date", isoDate);
      formData.append("duration", values.duration);
      formData.append("stream_type", values.streamType);
      formData.append("seller_id", userData._id);
      formData.append("description", values.description);
      if (values.categoryId) {
        formData.append("categoryId", values.categoryId);
      }

      // STAFF IDS (ARRAY)
      if (values.generateByAI && values.assignToStaff && values.staffMember && values.staffMember.length > 0) {
        formData.append("staff_ids", JSON.stringify(values.staffMember));
      }

      formData.append("generate_products_by_ai", values.generateByAI);

      if (!values.generateByAI && values.selectedProducts.length > 0) {
        formData.append("product_ids", JSON.stringify(values.selectedProducts));
      }

      // THUMBNAIL
      if (values.thumbnail) {
        formData.append("thumbnail", values.thumbnail);
        // no need to manually create {uri, type, name}
      }

      // SOCIAL MEDIA FIELDS
      if (values.youtubeEnabled && values.youtubeSelectedAccount) {
        formData.append(
          "youtube",
          JSON.stringify({
            status: true,
            mediaId: values.youtubeSelectedAccount,
          })
        );
      }

      if (values.twitchEnabled && values.twitchSelectedAccount) {
        formData.append(
          "twitch",
          JSON.stringify({
            status: true,
            mediaId: values.twitchSelectedAccount,
          })
        );
      }

      if (values.facebookEnabled && values.facebookSelectedAccount) {
        formData.append(
          "facebook",
          JSON.stringify({
            status: true,
            mediaId: values.facebookSelectedAccount,
          })
        );
      }

      // RECORDING VIDEO FILE
      if (values.streamType === "recorded" && values.videoType) {

        // --- MULTIPART UPLOAD LOGIC ---
        const file = values.videoType;
        const uploadStartTime = Date.now();
        const chunkSize = 5 * 1024 * 1024; // 5MB individual slices
        const totalChunks = Math.ceil(file.size / chunkSize);

        // 1. Init Upload
        const initRes = await initMultipartUpload({
          fileName: file.name,
          fileType: file.type
        });

        if (!initRes || !initRes.success) {
          throw new Error("Failed to initialize video upload");
        }

        const { uploadId, key } = initRes.data;
        const parts = [];

        // 2. Upload Chunks Array
        for (let i = 0; i < totalChunks; i++) {
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          const partNumber = i + 1;

          const urlParams = new URLSearchParams({
            uploadId,
            key,
            partNumber
          }).toString();

          const uploadRes = await uploadMultipartChunk(urlParams, chunk);
          if (!uploadRes.success) {
            throw new Error("Failed to upload video chunk number " + partNumber);
          }

          parts.push({
            ETag: uploadRes.data.ETag,
            PartNumber: uploadRes.data.PartNumber
          });

          const currentProgress = Math.round(((i + 1) / totalChunks) * 100);
          setUploadProgress(currentProgress);
        }

        // 3. Complete Upload
        const compRes = await completeMultipartUpload({
          uploadId,
          key,
          parts
        });

        if (!compRes.success) {
          throw new Error("Failed to complete multipart video upload");
        }

        // Return final video url via text to regular API formData
        formData.append("recorded_video", compRes.data.recording_url);

        setUploadProgress(100);

        // Ensure progress bar stays visible for at least 2 seconds
        const elapsedTime = Date.now() - uploadStartTime;
        if (elapsedTime < 2000) {
          await new Promise(resolve => setTimeout(resolve, 2000 - elapsedTime));
        }
      }


      // API CALL
      const response = await createStream(formData);

      if (response.success) {
        Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });

        if (values.scheduleType === "now") {
          setTimeout(() => {
            handleStartStream(response.data);
          }, 1000);
        }
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response),
          ...SWAL_SETTINGS,
        });
      }

      await loadStreams(search);

      formikRef.current?.resetForm();
      document.getElementById("schedule-a-stream")?.close();
      setPreview(null);
      setVideoPreview(null);

      if (fileInputRef.current) fileInputRef.current.value = "";
      const videoInput = document.querySelector('input[type="file"][accept="video/*"]');
      if (videoInput) videoInput.value = "";

    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", text: err?.message || t("streams.validation.something_went_wrong"), ...SWAL_SETTINGS });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewStream = (item) => {
    const sellerId = item.seller_id || item.seller?._id;
    navigate(`/user/live-streams/${sellerId}/${item.room_id}/${item._id}`);
  };

  const handleDeleteStream = (item) => {
    Swal.fire({
      customClass: "swal-wide",
      title: t("msg_are_you_sure", "Are you sure?"),
      text: t("you_not_be_able_to_revert_this", "You won't be able to revert this!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FF63B5",
      cancelButtonColor: "#6B7280",
      confirmButtonText: t("btn_yes_delete", "Yes, delete it!"),
      cancelButtonText: t("btn_cancel", "Cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        deleteStream(item._id)
          .then((response) => {
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message || t("msg_stream_deleted", "Stream deleted successfully"),
                ...SWAL_SETTINGS,
              });
              loadStreams(search);
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.error("delete stream error:", error);
            Swal.fire({
              icon: "error",
              text: error?.response?.data?.message || "Failed to delete stream",
              ...SWAL_SETTINGS,
            });
          });
      }
    });
  };
  const ScheduleAutoFill = ({ scheduleType, setFieldValue }) => {
    useEffect(() => {
      const now = new Date();

      if (scheduleType === "now") {
        const updated = new Date(now.getTime());

        setFieldValue("date", updated.toISOString().split("T")[0]);
        setFieldValue("time", updated.toTimeString().slice(0, 5));
      }

      if (scheduleType === "5min") {
        const updated = new Date(now.getTime() + 5 * 60000);

        setFieldValue("date", updated.toISOString().split("T")[0]);
        setFieldValue("time", updated.toTimeString().slice(0, 5));
      }

      if (scheduleType === "15min") {
        const updated = new Date(now.getTime() + 15 * 60000);

        setFieldValue("date", updated.toISOString().split("T")[0]);
        setFieldValue("time", updated.toTimeString().slice(0, 5));
      }

      if (scheduleType === "25min") {
        const updated = new Date(now.getTime() + 25 * 60000);

        setFieldValue("date", updated.toISOString().split("T")[0]);
        setFieldValue("time", updated.toTimeString().slice(0, 5));
      }

      if (scheduleType === "30min") {
        const updated = new Date(now.getTime() + 30 * 60000);

        setFieldValue("date", updated.toISOString().split("T")[0]);
        setFieldValue("time", updated.toTimeString().slice(0, 5));
      }

    }, [scheduleType]);

    return null;
  };

  return (
    <>


      <div className="dashbaord-bg py-10">
        <div className="container">
          <div className="d-flex align-center justify-between mb-8 text-zinc-950 border-b border-gray-200 pb-[15px] mb-[30px]">
            <h1 className="headings-h1 ">
              {t("streams.heading")}
            </h1>
            <button
              className="btn btn-pri !bg-[#FF63B5] !border-[#FF63B5] min-w-[175px]"
              onClick={() => {
                if (streamDetails?.live && streamDetails.live.length > 0) {
                  Swal.fire({
                    icon: "warning",
                    title: "Live Stream In Progress",
                    text: "A live stream is already going on. Please complete or end your current live stream before creating a new one.",
                    ...SWAL_SETTINGS
                  });
                  return;
                }
                const hoursLeft = data?.remaining_hours ?? userData?.remaining_hours ?? 0;
                if (hoursLeft <= 0) {
                  Swal.fire({
                    icon: "warning",
                    title: "Insufficient Hours",
                    text: "You have insufficient hours left. Please purchase hours first to schedule a stream.",
                    ...SWAL_SETTINGS
                  });
                  return;
                }
                document.getElementById("schedule-a-stream").showModal();
              }}
            >
              {t("streams.add_stream")}
            </button>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-[15px] mb-4">
            <h2 className="headings-h2 text-zinc-950">{t("streams.upcoming")}</h2>
            <div className="search-box relative flex-1 max-w-[425px]">
              <input
                type="text"
                className="form-control-custom !pr-[50px] min-w-[250px] md:min-w-[425px]"
                placeholder={t("streams.placeholder.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button className="absolute right-2 top-2">
                <img src="/images/search1.svg" alt="search" />
              </button>
            </div>
          </div>

          {!hasCamera && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 mb-6 animate-pulse">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-bold">{t("streams.camera_not_detected")}</p>
                <p className="text-sm">{t("streams.camera_not_detected_desc")}</p>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : streamDetails?.incoming?.length === 0 ? (
          <section className="upcoming-strems-section py-8 pt-0">
            <div className="container">
              <div className="upcoming-strems-des text-center flex items-center justify-center flex-col">
                <figure>
                  <img src="/images/video1.png" alt="video" />
                </figure>
                <figcaption className="flex flex-col gap-[10px] justify-center items-center">
                  <h2 className="heading-web-h3">{t("streams.no_upcoming_streams")}</h2>
                  <p className="body-md text-zinc-500 mb-2">
                    {t("streams.schedule_stream_to_connect")}
                  </p>
                  <button
                    className="btn btn-pri w-[fit-content]"
                    onClick={() => {
                      if (streamDetails?.live && streamDetails.live.length > 0) {
                        Swal.fire({
                          icon: "warning",
                          title: "Live Stream In Progress",
                          text: "A live stream is already going on. Please complete or end your current live stream before creating a new one.",
                          ...SWAL_SETTINGS
                        });
                        return;
                      }
                      const hoursLeft = data?.remaining_hours ?? userData?.remaining_hours ?? 0;
                      if (hoursLeft <= 0) {
                        Swal.fire({
                          icon: "warning",
                          title: "Insufficient Hours",
                          text: "You have insufficient hours left. Please purchase hours first to schedule a stream.",
                          ...SWAL_SETTINGS
                        });
                        return;
                      }
                      document.getElementById("schedule-a-stream").showModal();
                    }}
                  >
                    {t("streams.schedule_stream")}
                  </button>
                </figcaption>
              </div>
            </div>
          </section>) : (
          <section className="live-streaming-section bg-white py-8 md:py-12 lg:py-16 !pb-0">
            <div className="container">
              {streamDetails?.incoming && streamDetails?.incoming.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {streamDetails?.incoming?.map((item, index) => (
                    <div
                      key={item._id || index} className="cursor-pointer"
                    >
                      <Card
                        images={item.thumbnail}
                        title={item.title}
                        subtitle={t("streams.duration_minutes", { duration: item.duration })}
                        likeDelete={item.likeDelete}
                        time={item.duration}
                        dateTime={formatDateAndTime(item.date)}
                        button={t("streams.start", "Start")}
                        buttonDisabled={!hasCamera || new Date(item.date) > currentTime}
                        buttonTitle={
                          !hasCamera
                            ? t("streams.no_camera_access", "No camera detected")
                            : new Date(item.date) > currentTime
                            ? t("streams.stream_not_started_yet", "Stream start time has not arrived yet")
                            : ""
                        }
                        onButtonClick={() => {
                          if (hasCamera && new Date(item.date) <= currentTime) {
                            handleStartStream(item);
                          }
                        }}
                        editDelete={true}
                        onDelete={() => handleDeleteStream(item)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <p className="text-gray-500 text-lg font-medium">
                    {t("streams.no_live")}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="live-streaming-section bg-white py-8 md:py-12 lg:py-16 !pb-0">
          <div className="container">
            <div className="section-head flex justify-between items-center gap-[10px] mb-8 flex-wrap">
              <h2 className="heading-web-h2 text-[var(--black-scale-900)]">
                {t("streams.live")}
              </h2>
            </div>
            {streamDetails?.live && streamDetails?.live?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {streamDetails?.live?.map((item, index) => (
                  <div key={item._id || index}>
                    <Card
                      onClick={() => navigate(`/advertiser/streams/stream-details/${item?._id}`)}
                      images={item.thumbnail}
                      title={item.title}
                      subtitle={`Duration: ${item.duration} minutes`}
                      time={item.duration}
                      dateTime={formatDateAndTime(item.date)}
                      live={item.isLive}
                      views={item.views}
                      category={item.category}
                      button={t("streams.go_live")}
                      buttonDisabled={!hasCamera}
                      onButtonClick={() => handleStartStream(item)}
                      editDelete={true}
                      onDelete={() => handleDeleteStream(item)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500 text-lg font-medium">
                  {t("streams.no_live")}
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="live-streaming-section bg-white py-8 md:py-12 lg:py-16 !pb-0">
          <div className="container">
            <div className="section-head flex justify-between items-center gap-[10px] mb-8 flex-wrap">
              <h2 className="heading-web-h2 text-[var(--black-scale-900)]">
                {t("streams.past")}
              </h2>
            </div>
            {streamDetails?.past && streamDetails.past.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {streamDetails?.past?.map((item, index) => (
                    <div key={item._id || index}>
                      <Card
                        onClick={() => navigate(`/advertiser/streams/stream-details/${item?._id}`)}
                        images={item.thumbnail}
                        title={item.title}
                        time={item.duration}
                        subtitle={`Duration: ${item.duration} minutes`}
                        dateTime={formatDateAndTime(item.date)}
                        likeDelete={item.likeDelete}
                        button="Rewatch"
                        buttonDisabled={!item?.recording_url}
                        onButtonClick={() => handleViewStream(item)}
                        editDelete={true}
                        onDelete={() => handleDeleteStream(item)}
                      />
                    </div>
                  ))}
                </div>

                {isFetchingMorePast && (
                  <div className="flex justify-center items-center py-8">
                    <div className="w-8 h-8 border-3 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                <div ref={pastObserverTarget} className="h-6 w-full" />
              </>
            ) : (
              <div className="text-center py-10">
                <p className="text-gray-500 text-lg font-medium">
                  {t("streams.no_past_streams")}
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      <dialog id="schedule-a-stream" className="modal p-[15px]" aria-modal>
        <div className="modal-box max-w-[645px] !w-full">
          <h2 className="headings-h1 text-left text-zinc-800 mb-4">
            {t("streams.schedule_stream")}
          </h2>

          <Formik
            innerRef={formikRef}
            initialValues={{
              title: "",
              date: "",
              time: "",
              scheduleType: "now",
              duration: "",
              generateByAI: String(globalData?.show_ai_data_extraction) !== "false" ? ((data?.remaining_ai_clicks ?? userData?.remaining_ai_clicks ?? 0) > 0) : false,
              selectedProducts: [],
              assignToStaff: false,
              staffMember: "",
              streamType: "",
              description: "",
              thumbnail: null,
              youtube: null,
              facebook: null,
              twitch: null,
              categoryId: "",
              youtubeEnabled: false,
              youtubeSelectedAccount: "",
              facebookEnabled: false,
              facebookSelectedAccount: "",
              twitchEnabled: false,
              twitchSelectedAccount: "",
            }}
            validationSchema={ScheduleStreamSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, setFieldTouched, isSubmitting }) => {
              return (
                <Form className="max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                  <ScheduleAutoFill
                    scheduleType={values.scheduleType}
                    setFieldValue={setFieldValue}
                  />
                  {/* Title */}
                  <div className="form-group">
                    <label>{t("streams.title")}</label>
                    <Field
                      name="title"
                      type="text"
                      className="form-control-custom"
                      placeholder={t("streams.placeholder.title")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/^\s+/, ""); // only remove leading space
                        setFieldValue("title", value);
                      }}
                    />

                    <div className="text-sm text-red-600 mt-1">
                      <ErrorMessage name="title" />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t("streams.schedule_label")}</label>

                    <Field
                      as="select"
                      name="scheduleType"
                      className="form-control-custom"
                      onChange={(e) => {
                        const value = e.target.value;
                        setFieldValue("scheduleType", value);

                        // Optional: clear date/time when switching to custom
                        if (value === "custom") {
                          setFieldValue("date", "");
                          setFieldValue("time", "");
                        }
                      }}
                    >
                      <option value="now">{t("streams.schedule_now")}</option>
                      <option value="5min">{t("streams.5_min_later")}</option>
                      <option value="15min">{t("streams.15_min_later")}</option>
                      <option value="25min">{t("streams.25_min_later")}</option>
                      <option value="30min">{t("streams.30_min_later")}</option>
                      <option value="custom">{t("streams.custom")}</option>
                    </Field>
                  </div>
                  {/* Date + Time */}
                  {values.scheduleType === "custom" && (
                    <div className="grid grid-cols-2 gap-[15px] md:gap-[30px]">
                      {/* Date */}
                      <div className="form-group">
                        <label>{t("streams.date")}</label>
                        <Field name="date">
                          {({ field }) => (
                            <input
                              {...field}
                              type="date"
                              min={new Date().toISOString().split("T")[0]}
                              className="form-control-custom"
                              onChange={(e) => {
                                setFieldValue("date", e.target.value);
                                setFieldValue("time", ""); // reset time when date changes
                              }}
                            />
                          )}
                        </Field>


                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="date" />
                        </div>
                      </div>

                      {/* Time */}
                      {/* Time */}
                      <div className="form-group">
                        <label>{t("streams.time")}</label>

                        <Field name="time">
                          {({ field, form }) => {
                            const today = new Date().toISOString().split("T")[0];

                            let minTime = "";
                            if (form.values.date === today) {
                              const now = new Date();
                              const hours = String(now.getHours()).padStart(2, "0");
                              const minutes = String(now.getMinutes()).padStart(2, "0");
                              minTime = `${hours}:${minutes}`;
                            }

                            return (
                              <input
                                {...field}
                                type="time"
                                min={minTime}
                                className="form-control-custom"
                                onClick={(e) => {
                                  try {
                                    e.target.showPicker && e.target.showPicker();
                                  } catch (err) { }
                                }}
                              />
                            );
                          }}
                        </Field>

                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="time" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  <div className="form-group">
                    <label>{t("streams.duration")} ({t("common.duration_minutes_label", { defaultValue: "In Minutes" })})</label>
                    <Field
                      name="duration"
                      type="number"
                      className="form-control-custom"
                      placeholder={t("streams.placeholder.duration")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/^\s+/, ""); // only remove leading space
                        setFieldValue("duration", value);
                      }}
                    />
                    <div className="text-sm text-red-600 mt-1">
                      <ErrorMessage name="duration" />
                    </div>
                  </div>

                  {/* Product Category */}
                  <div className="form-group">
                    <label className="block mb-1">{t("products.category")}</label>
                    <CategorySelectDropdown
                      value={values.categoryId || ""}
                      onChange={(val) => {
                        setFieldValue("categoryId", val);
                        setFieldTouched("categoryId", true);
                      }}
                      options={categoryList}
                      placeholder={t("products.placeholder.select_category")}
                      fallbackIcon="/category.png"
                      capitalize={true}
                      showAllOption={false}
                    />
                    <div className="text-sm text-red-600 mt-1">
                      <ErrorMessage name="categoryId" />
                    </div>
                  </div>



                  {/* AI Generation Toggle */}
                  {String(globalData?.show_ai_data_extraction) !== "false" && (
                    <div className="form-group mb-4">
                      <div className="flex items-center justify-between">
                        <label className="!mb-0 font-bold text-gray-700">{t("streams.ai_gen_label")}</label>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${(data?.remaining_ai_clicks ?? userData?.remaining_ai_clicks ?? 0) > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {t("streams.clicks_remaining", { count: (data?.remaining_ai_clicks ?? userData?.remaining_ai_clicks ?? 0) })}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3">
                        <Field name="generateByAI">
                          {({ field, form }) => (
                            <>
                              <span className={`text-[10px] font-bold transition-colors duration-200 ${!field.value ? "text-gray-900" : "text-gray-400"}`}>OFF</span>
                              <input
                                type="checkbox"
                                {...field}
                                checked={field.value}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  const currentClicks = (data?.remaining_ai_clicks ?? userData?.remaining_ai_clicks ?? 0);
                                  if (isChecked && currentClicks <= 0) {
                                    Swal.fire({
                                      icon: "warning",
                                      title: "Insufficient AI Clicks",
                                      text: "You have 0 AI clicks remaining. Please purchase an AI package to enable this feature.",
                                      ...SWAL_SETTINGS
                                    });
                                    return;
                                  }
                                  form.setFieldValue("generateByAI", isChecked);
                                  if (!isChecked) {
                                    form.setFieldValue("assignToStaff", false);
                                    form.setFieldValue("staffMember", "");
                                  } else {
                                    form.setFieldValue("selectedProducts", []);
                                  }
                                }}
                                className="toggle border-indigo-600 bg-gray-200 checked:border-indigo-600 checked:bg-indigo-600 cursor-pointer"
                              />
                              <span className={`text-[10px] font-bold transition-colors duration-200 ${field.value ? "text-indigo-600" : "text-gray-400"}`}>ON</span>
                            </>
                          )}
                        </Field>
                      </div>
                      {((data?.remaining_ai_clicks ?? userData?.remaining_ai_clicks ?? 0) <= 0) && (
                        <p className="text-[10px] text-red-500 mt-1">{t("streams.purchase_ai_pkg")}</p>
                      )}
                    </div>
                  )}

                  {/* Manual Product Selection */}
                  {!values.generateByAI && (
                    <div className="form-group mb-4 relative" ref={productDropdownRef}>
                      <label className="font-bold text-gray-700 mb-2 block">{t("streams.select_products")}</label>

                      {/* Search Bar with Dropdown Arrow */}
                      <div className="relative mb-3 group">
                        <input
                          type="text"
                          className="form-control-custom !pr-[70px]"
                          placeholder={t("streams.search_products_placeholder")}
                          value={productSearch}
                          onFocus={() => setIsProductListOpen(true)}
                          onChange={(e) => {
                            setProductSearch(e.target.value);
                            setIsProductListOpen(true);
                          }}
                        />
                        <div className="absolute right-3 top-0 bottom-0 flex items-center pointer-events-none gap-2.5">
                          <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="w-[1px] h-4 bg-gray-200 flex-shrink-0" />
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isProductListOpen ? "rotate-180" : ""}`} />
                        </div>

                        {/* Styled Popup List - Moved Inside for better positioning */}
                        {isProductListOpen && (
                          <div className="absolute z-[100] top-full left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-[300px] flex flex-col overflow-hidden">
                            {/* Popup Header with Close Icon */}
                            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/50">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest pl-1">{t("streams.select_products")}</span>
                              <button
                                type="button"
                                onClick={() => setIsProductListOpen(false)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                title="Close"
                              >
                                <X className="w-4 h-4 text-black" />
                              </button>
                            </div>

                            {/* Scrollable List Area */}
                            <div className="overflow-y-auto custom-scrollbar" onScroll={handleProductScroll}>
                              {productsList.map((product) => {
                                const isSelected = values.selectedProducts.includes(product.value);
                                const isPub = product.isPublished;
                                return (
                                  <div
                                    key={product.value}
                                    onClick={() => {
                                      const next = isSelected
                                        ? values.selectedProducts.filter(id => id !== product.value)
                                        : [...values.selectedProducts, product.value];
                                      setFieldValue("selectedProducts", next);
                                    }}
                                    className={`px-3 py-2 cursor-pointer transition-colors flex items-center justify-between border-b border-gray-50 last:border-0
                                    ${isSelected ? "bg-[#e8f0fe] text-zinc-900 font-medium" : "hover:bg-[#f8fafc] text-zinc-700"}`}
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      {product.image ? (
                                        <img
                                          src={product.image}
                                          alt={product.label}
                                          className="w-10 h-10 object-cover rounded-md border border-gray-200 flex-shrink-0 bg-gray-50"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "/images/card1.png";
                                          }}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                                          <img
                                            src="/images/card1.png"
                                            alt="placeholder"
                                            className="w-6 h-6 opacity-40 object-cover"
                                          />
                                        </div>
                                      )}
                                      <div className="flex flex-col min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-sm font-medium text-zinc-900 truncate max-w-[200px]">{product.label}</span>
                                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold flex-shrink-0 ${isPub ? "bg-green-100 text-green-700 border border-green-200" : "bg-gray-100 text-gray-500 border border-gray-200"
                                            }`}>
                                            {isPub ? "Published" : "Unpublished"}
                                          </span>
                                        </div>
                                        {product.price && (
                                          <span className="text-xs text-gray-500 font-medium">${product.price}</span>
                                        )}
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                      </svg>
                                    )}
                                  </div>
                                );
                              })}
                              {loadingProducts && (
                                <div className="py-2.5 text-center text-xs text-gray-400 font-medium">
                                  Loading more products...
                                </div>
                              )}
                              {productsList.length === 0 && !loadingProducts && (
                                <div className="px-4 py-6 text-center text-gray-400 text-sm italic">
                                  {t("streams.no_results_found")}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Selected summary pills */}
                      {values.selectedProducts.length > 0 && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {t("streams.products_selected", { count: values.selectedProducts.length })}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {values.selectedProducts.map((id) => {
                              const product = productsList.find(p => p.value === id);
                              if (!product) return null;
                              return (
                                <span
                                  key={id}
                                  className="inline-flex items-center gap-1.5 bg-white border border-green-300 text-green-800 text-xs font-medium px-2 py-1 rounded-full shadow-sm"
                                >
                                  {product.image && (
                                    <img
                                      src={product.image}
                                      alt={product.label}
                                      className="w-4 h-4 object-cover rounded-full flex-shrink-0"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  {product.label}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFieldValue("selectedProducts", values.selectedProducts.filter(pid => pid !== id));
                                    }}
                                    className="ml-0.5 hover:text-red-500 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="text-sm text-red-600 mt-1">
                        <ErrorMessage name="selectedProducts" />
                      </div>
                    </div>
                  )}


                  {/* Assign to Staff Member Toggle */}
                  {values.generateByAI && (
                    <div className="form-group mb-4">
                      <label className="!mb-0 font-bold text-gray-700">{t("streams.assign_to_staff")}</label>
                      <div className="mt-1 flex items-center gap-3">
                        <Field name="assignToStaff">
                          {({ field, form }) => (
                            <>
                              <span className={`text-[10px] font-bold transition-colors duration-200 ${!field.value ? "text-gray-900" : "text-gray-400"}`}>{t("streams.off")}</span>
                              <input
                                type="checkbox"
                                {...field}
                                checked={field.value}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  form.setFieldValue("assignToStaff", isChecked);
                                  if (!isChecked) {
                                    form.setFieldValue("staffMember", "");
                                  }
                                }}
                                className="toggle border-indigo-600 bg-gray-200 checked:border-indigo-600 checked:bg-indigo-600"
                              />
                              <span className={`text-[10px] font-bold transition-colors duration-200 ${field.value ? "text-indigo-600" : "text-gray-400"}`}>{t("streams.on")}</span>
                            </>
                          )}
                        </Field>
                      </div>
                    </div>
                  )}

                  {/* Staff Member */}
                  {values.generateByAI && values.assignToStaff && (
                    <div className="form-group mb-4">
                      <label>{t("streams.staff_member")}</label>
                      <Field as="select" name="staffMember" className="form-control-custom">
                        <option value="">{t("streams.select_stream_type")}</option>

                        {staff?.map((item) => (
                          <option key={item._id} value={item._id}>
                            {item.name}
                          </option>
                        ))}
                      </Field>
                      <div className="text-sm text-red-600 mt-1">
                        <ErrorMessage name="staffMember" />
                      </div>
                    </div>
                  )}


                  {/* Stream Type */}
                  <div className="form-group">
                    <label>{t("streams.stream_type")}</label>
                    <Field as="select" name="streamType" className="form-control-custom">
                      <option value="">{t("streams.select_stream_type")}</option>
                      <option value="live">{t("common.live")}</option>
                      {String(globalData?.show_recorded_stream) !== "false" && (
                        <option value="recorded">{t("streams.recorded")}</option>
                      )}
                    </Field>
                    <div className="text-sm text-red-600 mt-1">
                      <ErrorMessage name="streamType" />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-group">
                    <label>{t("streams.description")}</label>
                    <Field
                      as="textarea"
                      name="description"
                      className="form-control-custom !h-[120px]"
                      placeholder={t("streams.placeholder.description")}
                      onChange={(e) => {
                        const value = e.target.value.replace(/^\s+/, ""); // only remove leading space
                        setFieldValue("description", value);
                      }}
                    />
                    <div className="text-sm text-red-600 mt-1">
                      <ErrorMessage name="description" />
                    </div>
                  </div>

                  {/* Thumbnail Upload */}
                  {/* Thumbnail Upload */}
                  <div className="form-group mb-4 flex flex-col">
                    <label>{t("streams.thumbnail")}</label>

                    {/* Hidden File Input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.currentTarget.files?.[0];
                        if (!file) return;

                        setFieldValue("thumbnail", file);

                        if (preview) URL.revokeObjectURL(preview);
                        const imageUrl = URL.createObjectURL(file);
                        setPreview(imageUrl);
                      }}
                    />

                    {/* Upload Button */}
                    {!preview && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="btn btn-outline !px-1 w-[150px] !text-[12px] !flex  justify-center items-center !py-1"
                      >
                        {t("streams.upload_thumbnail")}
                      </button>
                    )}
                    {/* Image Preview */}
                    {preview && (
                      <div className="mt-2 relative inline-block">
                        <img
                          src={preview}
                          alt={t("streams.thumbnail_preview")}
                          className="w-32 h-32 object-cover rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFieldValue("thumbnail", null);
                            setPreview(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-8 h-8 shadow-xl flex items-center justify-center hover:bg-red-700 transition-all z-[50] border-2 border-white"
                          title={t("streams.remove_thumbnail")}
                        >
                          <span className="font-bold text-sm leading-none">X</span>
                        </button>
                      </div>
                    )}

                    <div className="text-sm text-red-600 mt-1">
                      <ErrorMessage name="thumbnail" />
                    </div>
                  </div>

                  {/* Video Upload for Recorded Streams */}
                  {values.streamType === "recorded" && String(globalData?.show_recorded_stream) !== "false" && (
                    <div className="form-group mb-4">
                      <label>{t("streams.recorded_video")}</label>

                      {/* Hidden Input */}
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.currentTarget.files?.[0];
                          if (!file) return;

                          setFieldValue("videoType", file);

                          if (videoPreview) URL.revokeObjectURL(videoPreview);
                          setVideoPreview(URL.createObjectURL(file));
                        }}
                      />

                      {/* Upload Button */}
                      {!videoPreview && (
                        <button
                          type="button"
                          onClick={() => videoInputRef.current?.click()}
                          className="btn btn-outline px-1 w-[150px]  !text-[12px] !flex important justify-center items-center py-1"
                        >
                          {t("streams.upload_video")}
                        </button>
                      )}

                      {videoPreview && (
                        <div className="mt-2 relative inline-block group">
                          <video
                            controls
                            src={videoPreview}
                            className="w-32 h-32 object-cover rounded border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue("videoType", null);
                              setVideoPreview(null);
                              if (videoInputRef.current) {
                                videoInputRef.current.value = "";
                              }
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 shadow-lg hover:bg-red-600 transition-all flex items-center justify-center transform hover:scale-110 active:scale-95 z-20"
                            title="Remove video"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Connected Accounts Section */}
                  {/* <StreamOnAccountSection values={values} setFieldValue={setFieldValue} /> */}

                  {/* Connect Facebook / Twitch / Youtube */}
                  {values.streamType !== "recorded" && (
                    <>
                      {socialMediaData?.facebook?.enabled && String(globalData?.show_facebook_stream) !== "false" && (
                        <div className="bg-white rounded-[10px] border !border-[#F4F4F5] mb-[15px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)]">

                          {/* Header */}
                          <div className="flex items-center justify-between p-2">
                            <div className="flex items-center gap-3">
                              <img src="/images/f01.png" alt="facebook" />
                              <span className="font-medium">Facebook</span>
                            </div>

                            <input
                              type="checkbox"
                              checked={values.facebookEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked;

                                const firstAccountId =
                                  socialMediaData?.facebook?.config?.[0]?._id || "";

                                setFieldValue("facebookEnabled", enabled);
                                setFieldValue(
                                  "facebookSelectedAccount",
                                  enabled ? firstAccountId : ""
                                );

                                if (enabled && String(globalData?.show_multistream) === "false") {
                                  setFieldValue("twitchEnabled", false);
                                  setFieldValue("twitchSelectedAccount", "");
                                  setFieldValue("youtubeEnabled", false);
                                  setFieldValue("youtubeSelectedAccount", "");
                                }
                              }}
                              className="toggle border-indigo-600 bg-indigo-500 checked:border-orange-500 checked:bg-orange-400"
                            />
                          </div>

                          {/* Facebook Pages List */}
                          {values.facebookEnabled && socialMediaData?.facebook?.config?.length > 0 && (
                            <div className="px-3 pb-3 space-y-3">
                              {socialMediaData.facebook.config.map((acc) => (
                                <div
                                  key={acc._id}
                                  onClick={() =>
                                    setFieldValue("facebookSelectedAccount", acc._id)
                                  }
                                  className={`p-3 rounded-lg border flex items-center justify-between cursor-pointer transition
                  ${values.facebookSelectedAccount === acc._id
                                      ? "border-indigo-500 bg-indigo-50"
                                      : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                    }`}
                                >
                                  <span className="font-medium text-gray-800">
                                    {acc.pageName}
                                  </span>

                                  <input
                                    type="radio"
                                    name="facebookAccount"
                                    checked={values.facebookSelectedAccount === acc._id}
                                    readOnly
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}


                      {socialMediaData?.twitch?.enabled && String(globalData?.show_twitch_stream) !== "false" && (

                        <div className="bg-white rounded-[10px] border !border-[#F4F4F5] mb-[15px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)]">
                          <div className="flex items-center justify-between p-2">
                            <div className="flex items-center gap-3">
                              <img className="w-10 h-10" src="/images/twitch.png" alt="twitch" />
                              <span className="font-medium">Twitch</span>
                            </div>

                            <input
                              type="checkbox"
                              checked={values.twitchEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked;

                                const firstAccountId =
                                  socialMediaData?.twitch?.config?.[0]?._id || "";

                                setFieldValue("twitchEnabled", enabled);
                                setFieldValue(
                                  "twitchSelectedAccount",
                                  enabled ? firstAccountId : ""
                                );

                                if (enabled && String(globalData?.show_multistream) === "false") {
                                  setFieldValue("facebookEnabled", false);
                                  setFieldValue("facebookSelectedAccount", "");
                                  setFieldValue("youtubeEnabled", false);
                                  setFieldValue("youtubeSelectedAccount", "");
                                }
                              }}
                              className="toggle border-indigo-600 bg-indigo-500 checked:border-orange-500 checked:bg-orange-400" />
                          </div>

                          {values.twitchEnabled && socialMediaData?.twitch?.config?.length > 0 && (
                            <div className="px-3 pb-3 space-y-3">
                              {socialMediaData.twitch.config.map((acc) => (
                                <div
                                  key={acc._id}
                                  onClick={() =>
                                    setFieldValue("twitchSelectedAccount", acc._id)
                                  }
                                  className={`p-3 rounded-lg border flex items-center justify-between cursor-pointer transition
                              ${values.twitchSelectedAccount === acc._id
                                      ? "border-indigo-500 bg-indigo-50"
                                      : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                    }`}
                                >
                                  <span className="font-medium text-gray-800">
                                    {acc.accountName}
                                  </span>

                                  <input
                                    type="radio"
                                    name="twitchAccount"
                                    checked={values.twitchSelectedAccount === acc._id}
                                    readOnly
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {socialMediaData?.youtube?.enabled && String(globalData?.show_youtube_stream) !== "false" && (
                        <div className="bg-white rounded-[10px] border !border-[#F4F4F5] mb-[15px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)]">
                          <div className="flex items-center justify-between p-2">
                            <div className="flex items-center gap-3">
                              <img src="/images/f02.png" alt="youtube" />
                              <span className="font-medium">Youtube</span>
                            </div>

                            <input
                              type="checkbox"
                              checked={values.youtubeEnabled}
                              onChange={(e) => {
                                const enabled = e.target.checked;

                                const firstAccountId =
                                  socialMediaData?.youtube?.config?.[0]?._id || "";

                                setFieldValue("youtubeEnabled", enabled);
                                setFieldValue(
                                  "youtubeSelectedAccount",
                                  enabled ? firstAccountId : ""
                                );

                                if (enabled && String(globalData?.show_multistream) === "false") {
                                  setFieldValue("facebookEnabled", false);
                                  setFieldValue("facebookSelectedAccount", "");
                                  setFieldValue("twitchEnabled", false);
                                  setFieldValue("twitchSelectedAccount", "");
                                }
                              }}
                              className="toggle border-indigo-600 bg-indigo-500 checked:border-orange-500 checked:bg-orange-400 checked:text-orange-800"
                            />
                          </div>

                          {values.youtubeEnabled && socialMediaData?.youtube?.config?.length > 0 && (
                            <div className="px-3 pb-3 space-y-3">
                              {socialMediaData.youtube.config.map((acc) => (
                                <div
                                  key={acc._id}
                                  onClick={() =>
                                    setFieldValue("youtubeSelectedAccount", acc._id)
                                  }
                                  className={`p-3 rounded-lg border flex items-center justify-between cursor-pointer transition
                              ${values.youtubeSelectedAccount === acc._id
                                      ? "border-indigo-500 bg-indigo-50"
                                      : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                    }`}
                                >
                                  <span className="font-medium text-gray-800">
                                    {acc.accountName}
                                  </span>

                                  <input
                                    type="radio"
                                    name="youtubeAccount"
                                    checked={values.youtubeSelectedAccount === acc._id}
                                    readOnly
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                        </div>
                      )}



                      {/* <div className="flex items-center gap-3">
                            <img src="/images/instagram.svg" alt="instagram" />
                            <span className="font-medium">Instagram</span>
                          </div> */}

                      {/* <input
                              type="checkbox"
                              // checked={values.twitchEnabled}
//                               onChange={(e) => {
//                                 const enabled = e.target.checked;
// 
//                                 const firstAccountId =
//                                   socialMediaData?.twitch?.config?.[0]?._id || "";
// 
//                                 setFieldValue("twitchEnabled", enabled);
//                                 setFieldValue(
//                                   "twitchSelectedAccount",
//                                   enabled ? firstAccountId : ""
//                                 );
//                               }}
                              className="toggle border-indigo-600 bg-indigo-500 checked:border-orange-500 checked:bg-orange-400" /> */}
                      {/* <span className="text-gray-500">Coming soon</span> */}


                      {/* 
                          {values.twitchEnabled && data?.twitch?.config?.length > 0 && (
                            <div className="px-3 pb-3 space-y-3">
                              {data.twitch.config.map((acc) => (
                                <div
                                  key={acc._id}
                                  onClick={() =>
                                    setFieldValue("twitchSelectedAccount", acc._id)
                                  }
                                  className={`p-3 rounded-lg border flex items-center justify-between cursor-pointer transition
                              ${values.twitchSelectedAccount === acc._id
                                      ? "border-indigo-500 bg-indigo-50"
                                      : "border-gray-200 bg-gray-50 hover:bg-gray-100"
                                    }`}
                                >
                                  <span className="font-medium text-gray-800">
                                    {acc.accountName}
                                  </span>

                                  <input
                                    type="radio"
                                    name="twitchAccount"
                                    checked={values.twitchSelectedAccount === acc._id}
                                    readOnly
                                  />
                                </div>
                              ))}
                            </div>
                          )} */}

                    </>
                  )}
                  {/* </div> */}


                  {/* Submit */}
                  <button
                    type="submit"
                    className="btn btn-pri w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t("streams.processing") : t("streams.schedule_Pay")}
                  </button>

                </Form>
              );

            }}
          </Formik>
        </div>

        <form method="dialog">
          <button
            className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full"
            onClick={() => {
              formikRef.current?.resetForm();
              setPreview(null);


              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }

              const dialog = document.getElementById("schedule-a-stream");
              dialog?.close();
            }}
          >
            <img src="/images/close.svg" alt="close" />
            <span className="subtitle-2 bold">{t("streams.close")}</span>
          </button>

        </form>
      </dialog>
    </>
  );
}

export default Streams;
